import { IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { restartDepot } from '../../action/depotsCommands';
import { Depot } from '../../api/types';
import { thingListSelector } from '../../selector/depotsSelector';
import EditDepotDialog from './EditDepotDialog';
import RestartConfirmationDialog from './RestartConfirmationDialog';
import { RootState } from '../../store/store';
import { thingApiSlice } from '../../api/thing/thingApiSlice';
import CheckCircleIcon from '../../components/icons/CheckCircleIcon';
import CrossCircleIcon from '../../components/icons/CrossCircleIcon';
import TicketIcon from '../../components/icons/TicketIcon';
import SignalIcon from '../../components/icons/SignalIcon';
import PencilIcon from '../../components/icons/PencilIcon';
import { useFetchDeportLocations } from '../../flows/fetchDepotLocations';
import { formatAddressLine } from '../../utils/addressLine';

interface DepotInfoProps {
    depot: Depot;
}

const DepotInfo = (props: DepotInfoProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const thingList = useSelector(thingListSelector);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [restartConfirmationDialogOpen, setRestartConfirmationDialogOpen] =
        useState<boolean>(false);
    const thing = thingList.find((thing) => thing.id === props.depot.thingsId);
    const [getDeviceState] = thingApiSlice.useGetDeviceStateMutation();
    const [depotStatus, setDepotStatus] = useState<any>('unknown');
    const { selectedDepotDetails } = useSelector((state: RootState) => state.depotSlice);
    const fetchDepotLocations = useFetchDeportLocations(props.depot.locationId);

    const enable = selectedDepotDetails?.enabled;
    const bookable = selectedDepotDetails?.status.bookable;

    const triggerDepotRestart = () => {
        dispatch(restartDepot({ depotId: props.depot.id }));
        setRestartConfirmationDialogOpen(false);
    };

    useEffect(() => {
        const getDepotState = async() => {
            let status;
            if (thing) {
                try {
                    status = await getDeviceState({
                        deviceName: thing.name ?? '',
                    }).unwrap();
                    if ('error' in status) {
                        throw new Error(status.error);
                    }
                } catch (error) {
                    /* eslint-disable no-console */
                    console.log(error);
                }
            }
            setDepotStatus(status?.status ?? 'unknown');
        };
        getDepotState();
    }, [thing]);

    useEffect(() => {
        const fetchLocations = async () => {
            await fetchDepotLocations();
        };
        fetchLocations();
    }, []);

    return (
        <div className={'bg-text-contrast rounded-[5px] shadow-sm p-5'}>
            <div className={'flex w-full items-start justify-between mb-3'}>
                <div className={'min-w-0'}>
                    <h2 className={'truncate text-lg font-semibold text-foreground'}>
                        {props.depot.name}
                    </h2>
                    {selectedDepotDetails?.address && (
                        <span className={'text-foreground/60 text-sm truncate block'}>
                            {formatAddressLine(selectedDepotDetails.address)}
                        </span>
                    )}
                </div>
                <IconButton
                    className={'text-foreground p-1 -mr-2 shrink-0'}
                    onClick={() => setEditDialogOpen(true)}
                    title={t('location_details.edit_location_info')}
                    size={'small'}
                >
                    <PencilIcon />
                </IconButton>
            </div>
            <div className={'flex flex-col gap-1'}>
                <div className={'flex items-center gap-2 text-sm'}>
                    {enable === true ? (
                        <>
                            <CheckCircleIcon />
                            <span className={'text-[#27AE60]'}>
                                {t('depot_details.status.active')}
                            </span>
                        </>
                    ) : enable === false ? (
                        <>
                            <CrossCircleIcon />
                            <span className={'text-[#E72525]'}>
                                {t('depot_details.status.not_active')}
                            </span>
                        </>
                    ) : (
                        <>
                            <CrossCircleIcon />
                            <span className={'text-gray-400'}>
                                {t('depot_details.status.unknown')}
                            </span>
                        </>
                    )}
                </div>
                <div className={'flex items-center gap-2 text-sm'}>
                    {bookable === true ? (
                        <>
                            <TicketIcon className={'text-[#27AE60]'} />
                            <span className={'text-[#27AE60]'}>
                                {t('depot_details.status.bookable')}
                            </span>
                        </>
                    ) : bookable === false ? (
                        <>
                            <TicketIcon className={'text-[#E72525]'} />
                            <span className={'text-[#E72525]'}>
                                {t('depot_details.status.not_bookable')}
                            </span>
                        </>
                    ) : (
                        <>
                            <TicketIcon className={'text-gray-400'} />
                            <span className={'text-gray-400'}>
                                {t('depot_details.status.unknown')}
                            </span>
                        </>
                    )}
                </div>
                <div className={'flex items-center gap-2 text-sm'}>
                    {depotStatus === 'active' ? (
                        <>
                            <SignalIcon className={'text-[#27AE60]'} />
                            <span className={'text-[#27AE60]'}>
                                {t('depot_details.status.online')}
                            </span>
                        </>
                    ) : depotStatus === 'unknown' ? (
                        <>
                            <SignalIcon className={'text-gray-400'} />
                            <span className={'text-gray-400'}>
                                {t('depot_details.status.unknown')}
                            </span>
                        </>
                    ) : (
                        <>
                            <SignalIcon className={'text-[#E72525]'} />
                            <span className={'text-[#E72525]'}>
                                {t('depot_details.status.offline')}
                            </span>
                        </>
                    )}
                </div>
            </div>
            <EditDepotDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                depot={props.depot}
                isEditLocker={true}
            />
            <RestartConfirmationDialog
                open={restartConfirmationDialogOpen}
                depot={props.depot}
                onConfirm={triggerDepotRestart}
                onCancel={() => setRestartConfirmationDialogOpen(false)}
            />
        </div>
    );
};

export default DepotInfo;
